import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { saleApi, clientApi } from "../services/api";
import { useAuth } from "../context/AuthContext";
import Modal from "../components/Modal";
import {
  FiArrowLeft,
  FiPrinter,
  FiEdit,
  FiTrash2,
  FiFileText,
  FiUser,
  FiCheckCircle,
  FiAlertTriangle,
  FiBox,
  FiLayers,
  FiCheck,
  FiClock,
  FiCalendar,
  FiDollarSign,
  FiTag,
  FiExternalLink,
  FiShield,
  FiPhone,
  FiMail,
  FiMapPin,
  FiTrendingUp,
  FiList,
  FiTruck,
  FiRotateCcw,
} from "react-icons/fi";
import { toast } from "react-toastify";
import logoImg from "../assets/Icons/Logo.png";

// Helper to convert number to words for invoice total
const numberToWords = (num) => {
  if (num === null || num === undefined || isNaN(num)) return "Rupees Zero Only";
  const a = [
    "", "One ", "Two ", "Three ", "Four ", "Five ", "Six ", "Seven ", "Eight ", "Nine ",
    "Ten ", "Eleven ", "Twelve ", "Thirteen ", "Fourteen ", "Fifteen ", "Sixteen ", "Seventeen ", "Eighteen ", "Nineteen "
  ];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  const n = ("000000000" + Math.floor(Math.abs(num))).substr(-9);
  const match = n.match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
  if (!match) return "Rupees Zero Only";

  let str = "";
  str += Number(match[1]) !== 0 ? (a[Number(match[1])] || `${b[match[1][0]]} ${a[match[1][1]]}`) + "Crore " : "";
  str += Number(match[2]) !== 0 ? (a[Number(match[2])] || `${b[match[2][0]]} ${a[match[2][1]]}`) + "Lakh " : "";
  str += Number(match[3]) !== 0 ? (a[Number(match[3])] || `${b[match[3][0]]} ${a[match[3][1]]}`) + "Thousand " : "";
  str += Number(match[4]) !== 0 ? (a[Number(match[4])] || `${b[match[4][0]]} ${a[match[4][1]]}`) + "Hundred " : "";
  str += Number(match[5]) !== 0
    ? (str !== "" ? "and " : "") + (a[Number(match[5])] || `${b[match[5][0]]} ${a[match[5][1]]}`)
    : "";

  return str.trim() ? `Rupees ${str.trim()} Only` : "Rupees Zero Only";
};

// Format currency numbers nicely
const formatRs = (val) => {
  const num = parseFloat(val) || 0;
  return num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Format date into full month name, e.g. September 18, 2026 for DC Invoice
const formatFullDate = (d) => {
  if (!d) return "—";
  try {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return String(d);
    return dt.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  } catch (e) {
    return String(d);
  }
};

// Format month abbreviation for DC Reference number
const getDcMonth = (d) => {
  try {
    const dt = d ? new Date(d) : new Date();
    return dt.toLocaleString("en-US", { month: "short" }).toUpperCase();
  } catch (e) {
    return "DC";
  }
};

const SaleInvoicePage = () => {
  const { invoiceNumber } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [invoice, setInvoice] = useState(location.state?.invoice || null);
  const [loading, setLoading] = useState(!location.state?.invoice);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Client History & Records State
  const [clientHistory, setClientHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [clientFullData, setClientFullData] = useState(null);

  // 3 Options:
  // 1) "detail"        -> Detail Invoice (Har aek item ki complete detail, specs, serials & customer history with return breakdown)
  // 2) "dc_no_return"  -> DC Invoice Without Return (Original Delivery Challan slip as dispatched without return rows/deductions)
  // 3) "dc"            -> DC Invoice With Return (Delivery Challan slip with return rows and adjustments)
  const [selectedTemplate, setSelectedTemplate] = useState(() => {
    const saved = localStorage.getItem("invoice_selected_template");
    if (saved === "dc_no_return" || saved === "short" || saved === "single_page" || saved === "compact") return "dc_no_return";
    if (saved === "dc" || saved === "challan" || saved === "dc_with_return") return "dc";
    return "detail";
  });

  const handleTemplateChange = (tplId) => {
    setSelectedTemplate(tplId);
    localStorage.setItem("invoice_selected_template", tplId);
  };

  const canEdit = user?.role === "admin" || (Array.isArray(user?.permissions) && user.permissions.includes("sales"));

  const handleEditSale = (targetInv = invoice) => {
    if (!targetInv) return;
    navigate(`/sales?tab=record&editInvoice=${encodeURIComponent(targetInv.invoice_number)}`, {
      state: { invoice: targetInv },
    });
  };

  // Fetch client details & past records
  const fetchClientHistory = useCallback(async (clientId, clientName) => {
    if (!clientId && !clientName) return;
    try {
      setLoadingHistory(true);
      if (clientId) {
        try {
          const cRes = await clientApi.getById(clientId);
          const cData = cRes?.data || cRes;
          if (cData && (cData.company_name || cData.id)) {
            setClientFullData(cData);
          }
        } catch (e) {
          console.warn("Client details fetch fallback:", e);
        }

        const res = await saleApi.getAll({ client_id: clientId });
        const list = res?.data || (Array.isArray(res) ? res : []);
        setClientHistory(list);
      } else if (clientName) {
        const res = await saleApi.getAll({ search: clientName });
        const list = res?.data || (Array.isArray(res) ? res : []);
        const matched = list.filter(
          (s) => String(s.client_name).trim().toLowerCase() === String(clientName).trim().toLowerCase()
        );
        setClientHistory(matched.length > 0 ? matched : list);
      }
    } catch (err) {
      console.warn("Could not fetch client history:", err);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  const fetchInvoiceDetails = useCallback(async () => {
    try {
      setLoading(true);
      const decodedInvoiceNo = decodeURIComponent(invoiceNumber);
      const res = await saleApi.getById(decodedInvoiceNo);
      const s = res?.data?.data || res?.data || res;
      if (s && (s.id || s.invoice_number)) {
        const activeItemsSum = (s.items || []).reduce(
          (acc, it) => acc + (parseFloat(it.sale_price || it.price || it.unit_price) || 0) * (parseInt(it.quantity) || 1),
          0
        );
        const totalAmt = activeItemsSum > 0 ? activeItemsSum : (parseFloat(s.total_amount) || 0);

        const loadedInvoice = {
          ...s,
          total_price: totalAmt,
          items: s.items || [],
          return_items: s.return_items || [],
          returns: s.returns || [],
        };
        setInvoice(loadedInvoice);

        // Fetch client old records
        if (loadedInvoice.client_id || loadedInvoice.client_name) {
          fetchClientHistory(loadedInvoice.client_id, loadedInvoice.client_name);
        }

        const params = new URLSearchParams(location.search);
        if (params.get("edit") === "true") {
          navigate(`/sales?tab=record&editInvoice=${encodeURIComponent(loadedInvoice.invoice_number)}`, {
            replace: true,
            state: { invoice: loadedInvoice },
          });
        }
      }
    } catch (err) {
      console.error("Failed to fetch sale invoice details:", err);
      if (!location.state?.invoice) {
        toast.error(err.response?.data?.error || err.message || "Failed to load invoice details");
      }
    } finally {
      setLoading(false);
    }
  }, [invoiceNumber, location.state?.invoice, location.search, navigate, fetchClientHistory]);

  useEffect(() => {
    fetchInvoiceDetails();
  }, [fetchInvoiceDetails]);

  // Also trigger fetchClientHistory if invoice was passed via location.state
  useEffect(() => {
    if (invoice?.client_id || invoice?.client_name) {
      fetchClientHistory(invoice.client_id, invoice.client_name);
    }
  }, [invoice?.client_id, invoice?.client_name, fetchClientHistory]);

  const handlePrint = () => {
    window.print();
  };

  const handleDeleteInvoice = async () => {
    if (!invoice) return;
    try {
      setDeleting(true);
      const idToDelete = invoice.id || invoice.invoice_number;
      const res = await saleApi.delete(idToDelete);
      toast.success(res.data?.message || `Sale invoice #${invoice.invoice_number} deleted and items restored to inventory`);
      setDeleteModalOpen(false);
      navigate("/sales?tab=history");
    } catch (err) {
      console.error("Delete invoice error:", err);
      toast.error(err.response?.data?.error || err.message || "Failed to delete sale invoice");
    } finally {
      setDeleting(false);
    }
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return "—";
    try {
      if (typeof dateStr === "string" && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
        const parts = dateStr.slice(0, 10).split("-");
        const d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
        return d.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      }
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return String(dateStr);
      return d.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return String(dateStr);
    }
  };

  // Lifetime stats for this customer
  const clientLifetimeStats = useMemo(() => {
    const list = clientHistory || [];
    const totalInvoices = list.length;
    const totalSpent = list.reduce((sum, s) => {
      const amt = parseFloat(s.total_amount) || 0;
      if (amt > 0) return sum + amt;
      const itSum = (s.items || []).reduce(
        (isum, it) => isum + (parseFloat(it.sale_price || it.price || it.unit_price) || 0) * (parseInt(it.quantity) || 1),
        0
      );
      return sum + itSum;
    }, 0);
    const totalUnitsSold = list.reduce((sum, s) => {
      if (s.total_items_count) return sum + parseInt(s.total_items_count);
      return sum + (s.items || []).reduce((isum, it) => isum + (parseInt(it.quantity) || 1), 0);
    }, 0);

    return { totalInvoices, totalSpent, totalUnitsSold };
  }, [clientHistory]);

  const invoiceItems = invoice?.items || [];

  // Consolidate sale items with returns:
  // 1) Match returns against active sale items (by serial number or by item_name + category)
  // 2) For 100% returned items removed from active inventory_items, add them as returned rows
  const consolidatedItems = useMemo(() => {
    const rawItems = invoice?.items || [];
    const returnItems = invoice?.return_items || [];

    const map = new Map();

    rawItems.forEach((it) => {
      const isSerial = Boolean(it.serial_number && String(it.serial_number).trim() !== "");
      const cleanSn = isSerial ? String(it.serial_number).trim() : "";
      const rawItemName = (it.item_name || it.name || it.model || "Item").trim();
      const rawCat = (it.category_name || "").trim();
      const rawSubcat = (it.subcategory_name || it.type || "").trim();
      const rawPartNo = (it.part_number || it.sku || "").trim();
      const unitCost =
        parseFloat(
          it.sale_price !== null && it.sale_price !== undefined
            ? it.sale_price
            : (it.unit_price || it.price)
        ) || 0;

      const key = isSerial
        ? `sn_${cleanSn.toLowerCase()}`
        : `bulk_${rawItemName}_${it.category_id || rawCat}_${it.sub_category_id || rawSubcat}_${rawPartNo}_${unitCost}`.toLowerCase();

      const qty = parseInt(it.quantity, 10) || 1;

      if (!map.has(key)) {
        map.set(key, {
          key,
          id: it.id,
          item_name: rawItemName,
          category_name: rawCat || "General / IT Hardware",
          subcategory_name: rawSubcat,
          type: it.type || rawSubcat,
          serial_number: cleanSn || null,
          part_number: it.part_number || null,
          sku: it.sku || null,
          unit_price: unitCost,
          warranty_start_date: it.warranty_start_date,
          warranty_end_date: it.warranty_end_date,
          warranty_status: it.warranty_status,
          is_serial: isSerial,
          activeQuantity: 0,
          returnedQuantity: 0,
          returnedAmount: 0,
          returnsList: [],
        });
      }

      const grp = map.get(key);
      grp.activeQuantity += qty;
    });

    // Match return items under this invoice
    returnItems.forEach((ret) => {
      const isSerial = Boolean(ret.serial_number && String(ret.serial_number).trim() !== "");
      const cleanSn = isSerial ? String(ret.serial_number).trim() : "";
      const retName = (ret.item_name || "").trim().toLowerCase();
      const retQty = parseInt(ret.quantity, 10) || 1;
      const retPrice = parseFloat(ret.unit_price) || 0;
      const retAmt = parseFloat(ret.total_price) || (retQty * retPrice);

      let matched = false;
      if (isSerial) {
        const key = `sn_${cleanSn.toLowerCase()}`;
        if (map.has(key)) {
          const grp = map.get(key);
          grp.returnedQuantity += retQty;
          grp.returnedAmount += retAmt;
          grp.returnsList.push(ret);
          matched = true;
        }
      } else {
        for (const grp of map.values()) {
          if (!grp.is_serial && grp.item_name.toLowerCase() === retName) {
            grp.returnedQuantity += retQty;
            grp.returnedAmount += retAmt;
            grp.returnsList.push(ret);
            matched = true;
            break;
          }
        }
      }

      if (!matched) {
        // Serial or bulk item was 100% returned and cleared from active inventory_items!
        const retKey = isSerial ? `sn_${cleanSn.toLowerCase()}` : `returned_${ret.id}`;
        map.set(retKey, {
          key: retKey,
          id: ret.id,
          item_name: ret.item_name || "Returned Item",
          category_name: ret.category_name || "General / IT Hardware",
          subcategory_name: ret.subcategory_name || "",
          type: ret.subcategory_name || "",
          serial_number: cleanSn || null,
          part_number: ret.part_number || null,
          sku: ret.sku || null,
          unit_price: retPrice,
          warranty_start_date: null,
          warranty_end_date: null,
          warranty_status: null,
          is_serial: isSerial,
          activeQuantity: 0,
          returnedQuantity: retQty,
          returnedAmount: retAmt,
          returnsList: [ret],
        });
      }
    });

    return Array.from(map.values()).map((grp) => {
      const originalSoldQty = grp.activeQuantity + grp.returnedQuantity;
      const originalGrossAmt = originalSoldQty * grp.unit_price;
      const remainingQty = grp.activeQuantity;
      const remainingAmt = remainingQty * grp.unit_price;

      return {
        ...grp,
        originalSoldQty,
        originalGrossAmt,
        remainingQty,
        remainingAmt,
      };
    });
  }, [invoice?.items, invoice?.return_items]);

  const summaryTotals = useMemo(() => {
    let totalSoldUnits = 0;
    let totalSoldAmount = 0;
    let totalReturnedUnits = 0;
    let totalReturnedAmount = 0;
    let totalRemainingUnits = 0;
    let totalRemainingAmount = 0;

    consolidatedItems.forEach((it) => {
      totalSoldUnits += it.originalSoldQty;
      totalSoldAmount += it.originalGrossAmt;
      totalReturnedUnits += it.returnedQuantity;
      totalReturnedAmount += it.returnedAmount;
      totalRemainingUnits += it.remainingQty;
      totalRemainingAmount += it.remainingAmt;
    });

    const headerReturnedAmt = parseFloat(invoice?.returned_amount) || 0;
    if (totalReturnedAmount === 0 && headerReturnedAmt > 0) {
      totalReturnedAmount = headerReturnedAmt;
      totalRemainingAmount = Math.max(0, totalSoldAmount - totalReturnedAmount);
    }

    if (totalSoldAmount === 0) {
      totalSoldAmount = parseFloat(invoice?.gross_amount || invoice?.total_amount || 0);
      totalRemainingAmount = parseFloat(invoice?.total_amount || invoice?.total_price || 0);
    }

    return {
      totalSoldUnits,
      totalSoldAmount,
      totalReturnedUnits,
      totalReturnedAmount,
      totalRemainingUnits,
      totalRemainingAmount,
    };
  }, [consolidatedItems, invoice?.returned_amount, invoice?.gross_amount, invoice?.total_amount, invoice?.total_price]);

  const totalPrice = summaryTotals.totalRemainingAmount;
  const totalUnits = summaryTotals.totalSoldUnits;

  // Short Invoice: Group items with the same category and item name into a single row
  // Shows Category Name, Item Name, Count (sum of quantities), and Total Amount
  const shortInvoiceItems = useMemo(() => {
    const map = {};
    consolidatedItems.forEach((it) => {
      const rawCatName = (it.category_name || "").trim() || "General / IT Hardware";
      const rawItemName = (it.item_name || "Item").trim();
      const itemKey = `${rawCatName}___${rawItemName}`.toLowerCase();

      if (!map[itemKey]) {
        map[itemKey] = {
          categoryName: rawCatName,
          itemName: rawItemName,
          originalQty: 0,
          originalAmount: 0,
          returnedQty: 0,
          returnedAmount: 0,
          remainingQty: 0,
          remainingAmount: 0,
          returnsList: [],
        };
      }
      const grp = map[itemKey];
      grp.originalQty += it.originalSoldQty;
      grp.originalAmount += it.originalGrossAmt;
      grp.returnedQty += it.returnedQuantity;
      grp.returnedAmount += it.returnedAmount;
      grp.remainingQty += it.remainingQty;
      grp.remainingAmount += it.remainingAmt;
      if (it.returnsList && it.returnsList.length > 0) {
        grp.returnsList.push(...it.returnsList);
      }
    });
    return Object.values(map);
  }, [consolidatedItems]);

  // DC Invoice: Group items by category & item name
  // Un ma jo bhi item hon gy un k serial numbers aak row ma comma-separated show hon gy
  const dcInvoiceItems = useMemo(() => {
    const map = {};
    consolidatedItems.forEach((it) => {
      const rawCatName = (it.category_name || it.subcategory_name || "").trim() || "General / IT Hardware";
      const rawItemName = (it.item_name || "Item").trim();
      const rawPartNo = (it.part_number || it.sku || "").trim();
      const groupKey = `${rawCatName}___${rawItemName}`.toLowerCase();

      if (!map[groupKey]) {
        map[groupKey] = {
          categoryName: rawCatName,
          itemName: rawItemName,
          partNumber: rawPartNo,
          originalQty: 0,
          originalAmount: 0,
          returnedQty: 0,
          returnedAmount: 0,
          remainingQty: 0,
          remainingAmount: 0,
          returnsList: [],
          serials: [],
          returnedSerials: [],
        };
      }
      const grp = map[groupKey];
      if (!grp.partNumber && rawPartNo) grp.partNumber = rawPartNo;
      grp.originalQty += it.originalSoldQty;
      grp.originalAmount += it.originalGrossAmt;
      grp.returnedQty += it.returnedQuantity;
      grp.returnedAmount += it.returnedAmount;
      grp.remainingQty += it.remainingQty;
      grp.remainingAmount += it.remainingAmt;
      if (it.returnsList && it.returnsList.length > 0) {
        grp.returnsList.push(...it.returnsList);
      }
      if (it.serial_number) {
        if (it.returnedQuantity > 0 && it.activeQuantity === 0) {
          if (!grp.returnedSerials.includes(it.serial_number)) {
            grp.returnedSerials.push(it.serial_number);
          }
        } else {
          if (!grp.serials.includes(it.serial_number)) {
            grp.serials.push(it.serial_number);
          }
        }
      }
    });
    return Object.values(map);
  }, [consolidatedItems]);

  if (loading && !invoice) {
    return (
      <div style={{ padding: "60px 20px", textAlign: "center", color: "var(--slate-500)" }}>
        <div style={{ fontSize: "1.2rem", fontWeight: 700, marginBottom: 8, color: "var(--navy)" }}>
          Loading Sale Invoice...
        </div>
        <p>Fetching full details and sold inventory items.</p>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div style={{ padding: "60px 20px", textAlign: "center" }}>
        <h2 style={{ color: "var(--navy)", marginBottom: 12 }}>Invoice Not Found</h2>
        <p style={{ color: "var(--slate-500)", marginBottom: 24 }}>
          Could not find sale invoice #{decodeURIComponent(invoiceNumber || "")}.
        </p>
        <button
          type="button"
          onClick={() => navigate("/sales?tab=history")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "10px 20px",
            backgroundColor: "var(--primary)",
            color: "#fff",
            borderRadius: 8,
            border: "none",
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          <FiArrowLeft size={16} /> Back to Sales
        </button>
      </div>
    );
  }

  // =========================================================================
  // OPTION 1: DETAIL INVOICE (Har aek item ki full detail)
  // =========================================================================
  const renderDetailInvoiceTemplate = () => (
    <div
      className="invoice-paper"
      style={{
        maxWidth: 1000,
        margin: "0 auto",
        backgroundColor: "#fff",
        borderRadius: 14,
        border: "1px solid var(--slate-200)",
        boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
        padding: "40px 48px",
        color: "var(--slate-800)",
      }}
    >
      {/* HEADER */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          borderBottom: "2.5px solid var(--primary, #064E3B)",
          paddingBottom: 22,
          marginBottom: 24,
          gap: 20,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img
              src={logoImg}
              alt="Commercial IT Logo"
              style={{
                height: 60,
                maxHeight: 60,
                width: "auto",
                objectFit: "contain",
                borderRadius: 6,
              }}
            />

          </div>


        </div>

        <div style={{ textAlign: "right", minWidth: 220 }}>
          <div
            style={{
              fontSize: "1.35rem",
              fontWeight: 900,
              color: "var(--navy)",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            INVOICE# {invoice.invoice_number}
          </div>



        </div>
      </div>

      {/* METADATA CARDS: CLIENT PROFILE & INVOICE DETAILS */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: 20,
          marginBottom: 24,
        }}
      >
        {/* CLIENT FULL PROFILE */}
        <div
          style={{
            backgroundColor: "#F8FAFC",
            borderRadius: 10,
            border: "1px solid var(--slate-200)",
            padding: "18px 20px",
          }}
        >
          <div
            style={{
              fontSize: "0.74rem",
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              color: "var(--primary)",
              marginBottom: 10,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <FiUser size={14} /> Client Details & Account Profile
          </div>
          <div style={{ fontSize: "1.1rem", fontWeight: 800, color: "var(--navy)", marginBottom: 4 }}>
            {invoice.client_name || clientFullData?.company_name || "Direct Walk-in Client"}
          </div>

          <div style={{ fontSize: "0.82rem", color: "var(--slate-600)", lineHeight: "1.6" }}>
            {invoice.client_contact_person && (
              <div>
                <strong>Contact Person:</strong> {invoice.client_contact_person}
              </div>
            )}
            {invoice.client_contact && (
              <div>
                <strong>Phone / Mobile:</strong> {invoice.client_contact}
              </div>
            )}
            {invoice.client_email && (
              <div>
                <strong>Email:</strong> {invoice.client_email}
              </div>
            )}
            {invoice.client_ntn && (
              <div>
                <strong>NTN / Tax ID:</strong> {invoice.client_ntn}
              </div>
            )}
            {invoice.client_address && (
              <div>
                <strong>Address:</strong> {invoice.client_address}
              </div>
            )}
          </div>
        </div>

        {/* INVOICE & DISPATCH METADATA */}
        <div
          style={{
            backgroundColor: "#F8FAFC",
            borderRadius: 10,
            border: "1px solid var(--slate-200)",
            padding: "18px 20px",
          }}
        >
          <div
            style={{
              fontSize: "0.74rem",
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              color: "var(--navy)",
              marginBottom: 10,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <FiFileText size={14} /> Invoice & Dispatch Record
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "10px 14px",
              fontSize: "0.82rem",
            }}
          >
            <div>
              <span style={{ color: "var(--slate-500)", display: "block", fontSize: "0.72rem", fontWeight: 700 }}>
                INVOICE DATE
              </span>
              <span style={{ fontWeight: 700, color: "var(--navy)" }}>{formatDateDisplay(invoice.sale_date)}</span>
            </div>
            <div>
              <span style={{ color: "var(--slate-500)", display: "block", fontSize: "0.72rem", fontWeight: 700 }}>
                SALES OFFICER
              </span>
              <span style={{ fontWeight: 700, color: "var(--navy)" }}>
                {invoice.created_by_name || invoice.created_by_username || "Authorized Staff"}
              </span>
            </div>
            <div>
              <span style={{ color: "var(--slate-500)", display: "block", fontSize: "0.72rem", fontWeight: 700 }}>
                ITEMS IN THIS SALE
              </span>
              <span style={{ fontWeight: 800, color: "var(--primary)", fontSize: "0.92rem" }}>
                {totalUnits} {totalUnits === 1 ? "Unit" : "Units"} ({invoiceItems.length} {invoiceItems.length === 1 ? "Line" : "Lines"})
              </span>
            </div>
            <div>
              <span style={{ color: "var(--slate-500)", display: "block", fontSize: "0.72rem", fontWeight: 700 }}>
                PAYMENT STATUS
              </span>
              <span style={{ fontWeight: 700, color: "#15803D" }}>
                Completed / Paid
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ACTIVE INVOICE ITEM-BY-ITEM DETAILED TABLE */}
      <div style={{ marginBottom: 24, overflowX: "auto" }}>
        <div style={{ fontWeight: 800, fontSize: "0.92rem", color: "var(--navy)", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
          <FiBox size={15} style={{ color: "var(--primary)" }} /> Detail Invoice: Every Single Item Breakdown
        </div>
        <table
          className="invoice-table"
          style={{
            width: "100%",
            borderCollapse: "collapse",
            border: "1px solid var(--slate-200)",
            fontSize: "0.83rem",
          }}
        >
          <thead>
            <tr style={{ backgroundColor: "#F1F5F9", borderBottom: "1.5px solid var(--slate-300)", textAlign: "left" }}>
              <th style={{ padding: "9px 12px", width: 36, color: "var(--slate-600)", fontWeight: 700 }}>#</th>
              <th style={{ padding: "9px 12px", color: "var(--slate-700)", fontWeight: 700 }}>Item Description</th>
              <th style={{ padding: "9px 12px", color: "var(--slate-700)", fontWeight: 700 }}>Type</th>
              <th style={{ padding: "9px 12px", color: "var(--slate-700)", fontWeight: 700 }}>Serial Number</th>
              <th style={{ padding: "9px 12px", color: "var(--slate-700)", fontWeight: 700 }}>Part # / SKU</th>
              <th style={{ padding: "9px 12px", color: "var(--slate-700)", fontWeight: 700, textAlign: "center" }}>Qty</th>
              <th style={{ padding: "9px 12px", color: "var(--slate-700)", fontWeight: 700, textAlign: "right" }}>
                Unit Price (Rs.)
              </th>
              <th style={{ padding: "9px 12px", color: "var(--slate-700)", fontWeight: 700, textAlign: "right" }}>
                Total (Rs.)
              </th>
            </tr>
          </thead>
          <tbody>
            {consolidatedItems.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ padding: 25, textAlign: "center", color: "var(--slate-400)" }}>
                  No items found under this sale invoice.
                </td>
              </tr>
            ) : (
              consolidatedItems.map((it, idx) => {
                return (
                  <tr
                    key={it.key || idx}
                    style={{
                      borderBottom: "1px solid var(--slate-200)",
                      backgroundColor: idx % 2 === 0 ? "#fff" : "#FAFAFA",
                    }}
                  >
                    <td style={{ padding: "9px 12px", color: "var(--slate-400)", fontWeight: 600 }}>{idx + 1}</td>
                    <td style={{ padding: "9px 12px" }}>
                      <div style={{ fontWeight: 800, color: "var(--navy)", fontSize: "0.85rem" }}>
                        {it.item_name}
                      </div>
                    </td>
                    <td style={{ padding: "9px 12px", color: "var(--slate-600)", fontWeight: 600 }}>
                      {it.subcategory_name || it.type || "—"}
                    </td>
                    <td style={{ padding: "9px 12px" }}>
                      {it.serial_number ? (
                        <span
                          style={{
                            fontFamily: "monospace",
                            fontWeight: 800,
                            color: "var(--navy)",
                            backgroundColor: "#F1F5F9",
                            padding: "2px 7px",
                            borderRadius: 4,
                            border: "1px solid var(--slate-300)",
                            fontSize: "0.78rem",
                          }}
                        >
                          {it.serial_number}
                        </span>
                      ) : (
                        <span style={{ color: "var(--slate-400)" }}>— (Bulk)</span>
                      )}
                    </td>
                    <td style={{ padding: "9px 12px" }}>
                      <div style={{ fontSize: "0.76rem" }}>
                        {it.part_number && (
                          <div style={{ fontFamily: "monospace", color: "var(--slate-700)" }}>
                            PN: <strong>{it.part_number}</strong>
                          </div>
                        )}
                        {it.sku && (
                          <div style={{ fontFamily: "monospace", color: "var(--primary)" }}>
                            SKU: <strong>{it.sku}</strong>
                          </div>
                        )}
                        {!it.part_number && !it.sku && <span style={{ color: "var(--slate-400)" }}>—</span>}
                      </div>
                    </td>
                    <td style={{ padding: "9px 12px", textAlign: "center" }}>
                      <span
                        style={{
                          fontWeight: 800,
                          padding: "3px 8px",
                          borderRadius: 6,
                          backgroundColor: "#EEF2FF",
                          color: "#4338CA",
                          fontSize: "0.82rem",
                        }}
                      >
                        {it.originalSoldQty}
                      </span>
                    </td>
                    <td style={{ padding: "9px 12px", textAlign: "right" }}>
                      <span style={{ fontWeight: 700, color: "var(--slate-700)", fontSize: "0.85rem" }}>
                        Rs. {formatRs(it.unit_price)}
                      </span>
                    </td>
                    <td style={{ padding: "9px 12px", textAlign: "right" }}>
                      <span style={{ fontWeight: 800, color: "var(--navy)", fontSize: "0.88rem" }}>
                        Rs. {formatRs(it.originalGrossAmt)}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
          <tfoot>
            <tr style={{ backgroundColor: "#F8FAFC", borderTop: "2px solid var(--slate-300)" }}>
              <td colSpan={5} style={{ padding: "12px 10px", fontWeight: 800, color: "var(--navy)" }}>
                GRAND TOTAL
              </td>
              <td style={{ textAlign: "center", padding: "12px 10px", fontWeight: 900, color: "#065F46", fontSize: "0.95rem" }}>
                {summaryTotals.totalSoldUnits} {summaryTotals.totalSoldUnits === 1 ? "Unit" : "Units"}
              </td>
              <td></td>
              <td style={{ textAlign: "right", padding: "12px 10px", fontWeight: 900, color: "#15803D", fontSize: "1.05rem" }}>
                Rs. {formatRs(summaryTotals.totalSoldAmount)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* SUMMARY / TOTAL SECTION */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 20,
          marginBottom: 28,
          paddingTop: 10,
          borderTop: "1.5px solid var(--slate-200)",
        }}
      >
        <div style={{ maxWidth: 480, flex: 1 }}>
          <div style={{ fontSize: "0.74rem", color: "var(--slate-500)", fontWeight: 700, textTransform: "uppercase" }}>
            Amount Chargeable in Words
          </div>
          <div
            style={{
              fontSize: "0.92rem",
              fontWeight: 800,
              color: "var(--navy)",
              fontStyle: "italic",
              marginTop: 2,
              marginBottom: 12,
            }}
          >
            {numberToWords(summaryTotals.totalSoldAmount)}
          </div>

          <div
            style={{
              fontSize: "0.76rem",
              color: "var(--slate-500)",
              backgroundColor: "#F8FAFC",
              padding: "10px 14px",
              borderRadius: 8,
              border: "1px solid var(--slate-200)",
              lineHeight: "1.5",
            }}
          >
            <strong>Terms & Conditions:</strong> All items listed above carry official warranty as specified. Warranty does not cover physical damage, electrical surge, or burnout.
          </div>
        </div>

        <div
          style={{
            minWidth: 280,
            backgroundColor: "#F8FAFC",
            borderRadius: 10,
            border: "1px solid var(--slate-200)",
            padding: "16px 20px",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: "0.84rem" }}>
            <span style={{ color: "var(--slate-600)" }}>Total Quantity Sold:</span>
            <strong style={{ color: "var(--navy)" }}>{summaryTotals.totalSoldUnits} Units</strong>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: "0.84rem" }}>
            <span style={{ color: "var(--slate-600)" }}>Subtotal:</span>
            <strong style={{ color: "var(--navy)" }}>Rs. {formatRs(summaryTotals.totalSoldAmount)}</strong>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, fontSize: "0.84rem" }}>
            <span style={{ color: "var(--slate-600)" }}>Sales Tax / GST:</span>
            <span style={{ color: "var(--slate-500)", fontStyle: "italic" }}>Included</span>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              paddingTop: 10,
              borderTop: "2px solid var(--slate-300)",
              fontSize: "1.1rem",
              fontWeight: 900,
              color: "#15803D",
            }}
          >
            <span>Total Amount:</span>
            <span>Rs. {formatRs(summaryTotals.totalSoldAmount)}</span>
          </div>
        </div>
      </div>

      {/* SIGNATURES */}
      <div
        className="print-signature-section"
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 24,
          paddingTop: 36,
          marginTop: 24,
          borderTop: "1px dashed var(--slate-300)",
          textAlign: "center",
        }}
      >
        <div>
          <div style={{ borderBottom: "1px solid var(--slate-400)", height: 32, marginBottom: 6 }}></div>
          <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "var(--navy)" }}>Prepared / Billed By</div>
          <div style={{ fontSize: "0.72rem", color: "var(--slate-500)" }}>
            {invoice.created_by_name || "Sales Officer"}
          </div>
        </div>
        <div>
          <div style={{ borderBottom: "1px solid var(--slate-400)", height: 32, marginBottom: 6 }}></div>
          <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "var(--navy)" }}>Authorized Signatory</div>
          <div style={{ fontSize: "0.72rem", color: "var(--slate-500)" }}>Commercial-IT Management</div>
        </div>
        <div>
          <div style={{ borderBottom: "1px solid var(--slate-400)", height: 32, marginBottom: 6 }}></div>
          <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "var(--navy)" }}>Customer Sign & Stamp</div>
          <div style={{ fontSize: "0.72rem", color: "var(--slate-500)" }}>
            {invoice.client_name || "Client Representative"}
          </div>
        </div>
      </div>
    </div>
  );

  // =========================================================================
  // OPTION 2: SHORT INVOICE (Category Breakdown, Total Units & Net Total)
  // =========================================================================
  const renderShortInvoiceTemplate = () => (
    <div
      className="single-page-slip"
      style={{
        maxWidth: 820,
        margin: "0 auto",
        backgroundColor: "#fff",
        borderRadius: 10,
        border: "1.5px solid #CBD5E1",
        boxShadow: "0 6px 20px rgba(0,0,0,0.05)",
        padding: "24px 28px",
        color: "#0F172A",
        fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
      }}
    >
      {/* HEADER */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "2.5px solid #064E3B",
          paddingBottom: 12,
          marginBottom: 14,
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <img
              src={logoImg}
              alt="Commercial IT Logo"
              style={{
                height: 60,
                maxHeight: 60,
                width: "auto",
                objectFit: "contain",
                borderRadius: 4,
              }}
            />

          </div>

        </div>

        <div style={{ textAlign: "right" }}>
          <div
            style={{
              fontSize: "1.15rem",
              fontWeight: 900,
              color: "#0F172A",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            SHORT SALES INVOICE
          </div>
          <div
            style={{
              display: "inline-block",
              fontFamily: "monospace",
              fontWeight: 800,
              fontSize: "1rem",
              color: "#1D4ED8",
              backgroundColor: "#EFF6FF",
              padding: "2px 8px",
              borderRadius: 4,
              border: "1px solid #DBEAFE",
              marginTop: 2,
            }}
          >
            #{invoice.invoice_number}
          </div>
          <div style={{ fontSize: "0.76rem", color: "#475569", marginTop: 2 }}>
            <strong>Date:</strong> {formatDateDisplay(invoice.sale_date)} | <strong style={{ color: "#166534" }}>PAID</strong>
          </div>
        </div>
      </div>

      {/* CLIENT INFORMATION BAR */}
      <div
        style={{
          backgroundColor: "#F8FAFC",
          borderRadius: 8,
          border: "1px solid #E2E8F0",
          padding: "10px 14px",
          marginBottom: 16,
          display: "grid",
          gridTemplateColumns: "1.2fr 0.8fr",
          gap: 12,
          fontSize: "0.78rem",
          lineHeight: 1.5,
        }}
      >
        <div>
          <div>
            <span style={{ color: "#64748B" }}>Customer / M/S: </span>
            <strong style={{ color: "#0F172A", fontSize: "0.85rem" }}>
              {invoice.client_name || clientFullData?.company_name || "Direct Walk-in Client"}
            </strong>
          </div>
          {(invoice.client_contact_person || invoice.client_contact) && (
            <div>
              <span style={{ color: "#64748B" }}>Attn / Contact: </span>
              <strong>{invoice.client_contact_person || "—"}</strong>
              {invoice.client_contact && <span> ({invoice.client_contact})</span>}
            </div>
          )}
        </div>

        <div>
          <div>
            <span style={{ color: "#64748B" }}>NTN / Tax ID: </span>
            <strong>{invoice.client_ntn || clientFullData?.ntn || "—"}</strong>
          </div>
          <div>
            <span style={{ color: "#64748B" }}>Address: </span>
            <span>{invoice.client_address || clientFullData?.address || "Karachi, Pakistan"}</span>
          </div>
        </div>
      </div>



      {/* ITEMS BREAKDOWN TABLE (GROUPED BY ITEM NAME) */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: "0.85rem", fontWeight: 800, color: "#0F172A", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
          <FiLayers size={14} style={{ color: "#064E3B" }} /> Items & Category Summary Breakdown
        </div>
        <table
          className="compact-table"
          style={{
            width: "100%",
            borderCollapse: "collapse",
            border: "1.5px solid #CBD5E1",
            fontSize: "0.82rem",
          }}
        >
          <thead>
            <tr style={{ backgroundColor: "#F1F5F9", borderBottom: "2px solid #94A3B8", textAlign: "left" }}>
              <th style={{ width: 38, padding: "8px 10px", textAlign: "center", color: "#334155" }}>#</th>
              <th style={{ width: 160, padding: "8px 12px", color: "#334155" }}>Category Name</th>
              <th style={{ padding: "8px 12px", color: "#334155" }}>Item Name</th>
              <th style={{ width: 120, padding: "8px 12px", textAlign: "center", color: "#334155", fontWeight: 800 }}>
                Count (Qty)
              </th>
              <th style={{ width: 160, padding: "8px 12px", textAlign: "right", color: "#334155" }}>
                Amount (Rs.)
              </th>
            </tr>
          </thead>
          <tbody>
            {shortInvoiceItems.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ padding: 16, textAlign: "center", color: "#94A3B8" }}>
                  No items found in this invoice.
                </td>
              </tr>
            ) : (
              shortInvoiceItems.map((item, idx) => {
                const hasReturn = item.returnedQty > 0;
                return (
                  <React.Fragment key={item.itemName || idx}>
                    <tr
                      style={{
                        borderBottom: hasReturn ? "1px dashed #CBD5E1" : "1px solid #E2E8F0",
                        backgroundColor: idx % 2 === 0 ? "#fff" : "#FAFAFA",
                      }}
                    >
                      <td style={{ textAlign: "center", padding: "8px 10px", color: "#64748B", fontWeight: 600 }}>
                        {idx + 1}
                      </td>
                      <td style={{ padding: "8px 12px", color: "#475569", fontWeight: 600 }}>
                        {item.categoryName}
                      </td>
                      <td style={{ padding: "8px 12px" }}>
                        <strong style={{ color: "#0F172A", fontSize: "0.86rem" }}>{item.itemName}</strong>
                      </td>
                      <td
                        style={{
                          textAlign: "center",
                          padding: "8px 12px",
                          fontWeight: 800,
                          color: hasReturn ? "#1D4ED8" : "#1E3A8A",
                        }}
                      >
                        {item.originalQty} {item.originalQty === 1 ? "Unit" : "Units"}
                      </td>
                      <td
                        style={{
                          textAlign: "right",
                          padding: "8px 12px",
                          fontWeight: 800,
                          color: "#0F172A",
                        }}
                      >
                        Rs. {formatRs(item.originalAmount)}
                      </td>
                    </tr>

                    {/* Next Row: Returned by Client */}
                    {hasReturn && (
                      <tr style={{ backgroundColor: "#FEF2F2", borderBottom: "1px dashed #FECACA" }}>
                        <td></td>
                        <td colSpan={2} style={{ padding: "6px 12px" }}>
                          <div style={{ color: "#DC2626", fontSize: "0.78rem", fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
                            <FiRotateCcw size={12} style={{ flexShrink: 0 }} />
                            <span>
                              Returned by Client
                              {item.returnsList && item.returnsList.length > 0 && (
                                <span style={{ fontFamily: "monospace", fontSize: "0.74rem", color: "#B91C1C", marginLeft: 6 }}>
                                  ({item.returnsList.map((r) => (r.return_number ? `#${r.return_number}` : "")).filter(Boolean).join(", ")})
                                </span>
                              )}
                            </span>
                          </div>
                        </td>
                        <td style={{ textAlign: "center", padding: "6px 12px" }}>
                          <span
                            style={{
                              fontWeight: 800,
                              padding: "2px 7px",
                              borderRadius: 4,
                              backgroundColor: "#FEE2E2",
                              color: "#DC2626",
                              fontSize: "0.80rem",
                            }}
                          >
                            -{item.returnedQty} Ret
                          </span>
                        </td>
                        <td style={{ textAlign: "right", padding: "6px 12px", fontWeight: 800, color: "#DC2626" }}>
                          - Rs. {formatRs(item.returnedAmount)}
                        </td>
                      </tr>
                    )}

                    {/* Then Row: Net Remaining Sale */}
                    {hasReturn && (
                      <tr style={{ backgroundColor: "#F0FDF4", borderBottom: "1.5px solid #CBD5E1" }}>
                        <td></td>
                        <td colSpan={2} style={{ padding: "6px 12px" }}>
                          <div style={{ color: "#15803D", fontSize: "0.80rem", fontWeight: 800, display: "flex", alignItems: "center", gap: 6 }}>
                            <FiCheckCircle size={12} style={{ flexShrink: 0 }} />
                            <span>Net Retained Sale</span>
                          </div>
                        </td>
                        <td style={{ textAlign: "center", padding: "6px 12px" }}>
                          <span
                            style={{
                              fontWeight: 800,
                              padding: "2px 7px",
                              borderRadius: 4,
                              backgroundColor: "#DCFCE7",
                              color: "#15803D",
                              fontSize: "0.82rem",
                            }}
                          >
                            {item.remainingQty} {item.remainingQty === 1 ? "Unit" : "Units"}
                          </span>
                        </td>
                        <td style={{ textAlign: "right", padding: "6px 12px", fontWeight: 900, color: "#15803D" }}>
                          Rs. {formatRs(item.remainingAmount)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
          <tfoot>
            <tr style={{ backgroundColor: "#F8FAFC", borderTop: "2px solid #94A3B8" }}>
              <td colSpan={3} style={{ padding: "10px 12px", fontWeight: 900, color: "#0F172A" }}>
                {summaryTotals.totalReturnedUnits > 0 ? "TOTAL SUMMARY (NET ACTIVE)" : "TOTAL SUMMARY"}
              </td>
              <td style={{ textAlign: "center", padding: "10px 12px", fontWeight: 900, color: "#1D4ED8", fontSize: "0.95rem" }}>
                {summaryTotals.totalRemainingUnits} Units
              </td>
              <td style={{ textAlign: "right", padding: "10px 12px", fontWeight: 900, color: "#15803D", fontSize: "1.05rem" }}>
                Net Total: Rs. {formatRs(summaryTotals.totalRemainingAmount)}
              </td>
            </tr>
            <tr style={{ backgroundColor: "#fff", borderTop: "1px solid #CBD5E1" }}>
              <td colSpan={5} style={{ padding: "10px 12px" }}>
                <span style={{ fontSize: "0.75rem", fontWeight: 800, color: "#64748B", textTransform: "uppercase", marginRight: 8 }}>
                  Amount Chargeable in Words:
                </span>
                <span style={{ fontSize: "0.85rem", fontWeight: 800, color: "#0F172A", fontStyle: "italic" }}>
                  {numberToWords(summaryTotals.totalRemainingAmount)}
                </span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div style={{ fontSize: "0.72rem", color: "#64748B", marginTop: 4, marginBottom: 16 }}>
        Short sales slip issued for commercial dispatch & reference. Detailed item breakdown available on Detail Invoice.
      </div>

      {/* SIGNATURES */}
      <div
        className="print-signature-section"
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 20,
          paddingTop: 20,
          textAlign: "center",
          fontSize: "0.76rem",
        }}
      >
        <div>
          <div style={{ borderBottom: "1px solid #94A3B8", height: 24, marginBottom: 4 }}></div>
          <strong>Prepared By:</strong> {invoice.created_by_name || "Sales Counter"}
        </div>
        <div>
          <div style={{ borderBottom: "1px solid #94A3B8", height: 24, marginBottom: 4 }}></div>
          <strong>Authorized Signatory:</strong> Commercial-IT
        </div>
        <div>
          <div style={{ borderBottom: "1px solid #94A3B8", height: 24, marginBottom: 4 }}></div>
          <strong>Customer Signature:</strong> {invoice.client_name || "Client"}
        </div>
      </div>
    </div>
  );

  // =========================================================================
  // OPTION 3: DC INVOICE (Delivery Challan - Commercial-IT Brand Format)
  // Supports withReturn = true (With Return adjustments) and withReturn = false (Without Return / Original Dispatch)
  // =========================================================================
  const renderDcInvoiceTemplate = (withReturn = true) => {
    const activeTotal = withReturn ? summaryTotals.totalRemainingAmount : summaryTotals.totalSoldAmount;
    const activeUnits = withReturn ? summaryTotals.totalRemainingUnits : summaryTotals.totalSoldUnits;

    return (
      <div
        className="invoice-paper"
        style={{
          maxWidth: 1000,
          margin: "0 auto",
          backgroundColor: "#fff",
          borderRadius: 14,
          border: "1px solid var(--slate-200)",
          boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
          padding: "40px 48px",
          color: "var(--slate-800)",
        }}
      >
        {/* HEADER */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            borderBottom: "2.5px solid var(--primary, #064E3B)",
            paddingBottom: 22,
            marginBottom: 24,
            gap: 20,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <img
                src={logoImg}
                alt="Commercial IT Logo"
                style={{
                  height: 60,
                  maxHeight: 60,
                  width: "auto",
                  objectFit: "contain",
                  borderRadius: 6,
                }}
              />

            </div>

          </div>

          <div style={{ textAlign: "right", minWidth: 220 }}>
            <div
              style={{
                fontSize: "1.35rem",
                fontWeight: 900,
                color: "var(--navy)",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              INVOICE #{invoice.invoice_number}
            </div>



          </div>
        </div>

        {/* METADATA CARDS: CLIENT PROFILE & DISPATCH DETAILS */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: 20,
            marginBottom: 24,
          }}
        >
          {/* CLIENT FULL PROFILE */}
          <div
            style={{
              backgroundColor: "#F8FAFC",
              borderRadius: 10,
              border: "1px solid var(--slate-200)",
              padding: "18px 20px",
            }}
          >
            <div
              style={{
                fontSize: "0.74rem",
                fontWeight: 800,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: "var(--primary)",
                marginBottom: 10,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <FiUser size={14} /> Client Details & Delivery Destination
            </div>
            <div style={{ fontSize: "1.1rem", fontWeight: 800, color: "var(--navy)", marginBottom: 4 }}>
              {invoice.client_name || clientFullData?.company_name || "Direct Walk-in Client"}
            </div>

            <div style={{ fontSize: "0.82rem", color: "var(--slate-600)", lineHeight: "1.6" }}>
              {invoice.client_contact_person && (
                <div>
                  <strong>Contact Person:</strong> {invoice.client_contact_person}
                </div>
              )}
              {invoice.client_contact && (
                <div>
                  <strong>Phone / Mobile:</strong> {invoice.client_contact}
                </div>
              )}
              {invoice.client_email && (
                <div>
                  <strong>Email:</strong> {invoice.client_email}
                </div>
              )}
              {invoice.client_ntn && (
                <div>
                  <strong>NTN / Tax ID:</strong> {invoice.client_ntn}
                </div>
              )}
              {invoice.client_address && (
                <div>
                  <strong>Delivery Address:</strong> {invoice.client_address}
                </div>
              )}
            </div>
          </div>

          {/* DISPATCH & RECORD DETAILS */}
          <div
            style={{
              backgroundColor: "#F8FAFC",
              borderRadius: 10,
              border: "1px solid var(--slate-200)",
              padding: "18px 20px",
            }}
          >
            <div
              style={{
                fontSize: "0.74rem",
                fontWeight: 800,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: "var(--navy)",
                marginBottom: 10,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <FiFileText size={14} /> Dispatch & Shipment Record
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "10px 14px",
                fontSize: "0.82rem",
              }}
            >
              <div>
                <span style={{ color: "var(--slate-500)", display: "block", fontSize: "0.72rem", fontWeight: 700 }}>
                  BILL REF#
                </span>
                <span style={{ fontWeight: 800, color: "var(--navy)", fontFamily: "monospace" }}>
                  #{invoice.invoice_number}
                </span>
              </div>
              <div>
                <span style={{ color: "var(--slate-500)", display: "block", fontSize: "0.72rem", fontWeight: 700 }}>
                  DISPATCH DATE
                </span>
                <span style={{ fontWeight: 700, color: "var(--navy)" }}>{formatDateDisplay(invoice.sale_date)}</span>
              </div>
              <div>
                <span style={{ color: "var(--slate-500)", display: "block", fontSize: "0.72rem", fontWeight: 700 }}>
                  DISPATCH OFFICER
                </span>
                <span style={{ fontWeight: 700, color: "var(--navy)" }}>
                  {invoice.created_by_name || invoice.created_by_username || "Authorized Staff"}
                </span>
              </div>
              <div>
                <span style={{ color: "var(--slate-500)", display: "block", fontSize: "0.72rem", fontWeight: 700 }}>
                  TOTAL UNITS
                </span>
                <span style={{ fontWeight: 800, color: "var(--primary)", fontSize: "0.92rem" }}>
                  {withReturn ? summaryTotals.totalSoldUnits : activeUnits} {activeUnits === 1 ? "Unit" : "Units"}
                  {withReturn && summaryTotals.totalReturnedUnits > 0 && (
                    <span style={{ fontSize: "0.75rem", color: "#15803D", marginLeft: 6 }}>
                      ({summaryTotals.totalRemainingUnits} Active)
                    </span>
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ITEMS & SERIALS TABLE */}
        <div style={{ marginBottom: 24, overflowX: "auto" }}>
          <div style={{ fontWeight: 800, fontSize: "0.92rem", color: "var(--navy)", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
            <FiTruck size={15} style={{ color: "var(--primary)" }} /> Delivery Items & Serial Numbers
          </div>
          <table
            className="invoice-table"
            style={{
              width: "100%",
              borderCollapse: "collapse",
              border: "1px solid var(--slate-200)",
              fontSize: "0.83rem",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#F1F5F9", borderBottom: "1.5px solid var(--slate-300)", textAlign: "left" }}>
                <th style={{ padding: "9px 12px", width: 40, color: "var(--slate-600)", fontWeight: 700, textAlign: "center" }}>Sr.#</th>
                <th style={{ padding: "9px 12px", width: 140, color: "var(--slate-700)", fontWeight: 700 }}>Category</th>
                <th style={{ padding: "9px 12px", color: "var(--slate-700)", fontWeight: 700 }}>Descriptions of Goods</th>
                <th style={{ padding: "9px 12px", width: 80, color: "var(--slate-700)", fontWeight: 700, textAlign: "center" }}>Qty.</th>
                <th style={{ padding: "9px 12px", width: 130, color: "var(--slate-700)", fontWeight: 700, textAlign: "right" }}>
                  Amount (Rs.)
                </th>
              </tr>
            </thead>
            <tbody>
              {dcInvoiceItems.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: 25, textAlign: "center", color: "var(--slate-400)" }}>
                    No items found under this sale invoice.
                  </td>
                </tr>
              ) : (
                dcInvoiceItems.map((grp, idx) => {
                  const hasReturn = withReturn && grp.returnedQty > 0;
                  const displaySerials = withReturn
                    ? grp.serials
                    : [...grp.serials, ...(grp.returnedSerials || [])];

                  return (
                    <React.Fragment key={idx}>
                      <tr
                        style={{
                          borderBottom: hasReturn ? "1px dashed #CBD5E1" : "1px solid #E2E8F0",
                          backgroundColor: idx % 2 === 0 ? "#fff" : "#FAFAFA",
                        }}
                      >
                        <td style={{ padding: "10px 12px", color: "var(--slate-500)", fontWeight: 700, textAlign: "center" }}>
                          {idx + 1}
                        </td>
                        <td style={{ padding: "10px 12px", color: "var(--slate-700)", fontWeight: 600 }}>
                          {grp.categoryName}
                        </td>
                        <td style={{ padding: "10px 12px" }}>
                          <div style={{ fontWeight: 800, color: "var(--navy)", fontSize: "0.86rem" }}>
                            {grp.itemName}
                          </div>
                          {grp.partNumber && (
                            <div style={{ fontSize: "0.75rem", color: "var(--slate-500)", fontFamily: "monospace", marginTop: 2 }}>
                              P/N: <strong>{grp.partNumber}</strong>
                            </div>
                          )}
                        </td>
                        <td style={{ padding: "10px 12px", textAlign: "center" }}>
                          <span
                            style={{
                              fontWeight: 800,
                              padding: "3px 9px",
                              borderRadius: 6,
                              backgroundColor: hasReturn ? "#EFF6FF" : "#EEF2FF",
                              color: hasReturn ? "#1D4ED8" : "#4338CA",
                              fontSize: "0.83rem",
                            }}
                          >
                            {grp.originalQty}
                          </span>
                        </td>
                        <td style={{ padding: "10px 12px", textAlign: "right" }}>
                          <span style={{ fontWeight: 800, color: "var(--navy)", fontSize: "0.88rem" }}>
                            Rs. {formatRs(grp.originalAmount)}
                          </span>
                        </td>
                      </tr>

                      {/* Next Row: Returned Goods (Only when withReturn is true) */}
                      {hasReturn && (
                        <tr style={{ backgroundColor: "#FEF2F2", borderBottom: "1px dashed #FECACA" }}>
                          <td></td>
                          <td colSpan={2} style={{ padding: "8px 12px" }}>
                            <div style={{ color: "#DC2626", fontSize: "0.80rem", fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
                              <FiRotateCcw size={12} style={{ flexShrink: 0 }} />
                              <span>
                                Returned Goods
                                {grp.returnsList && grp.returnsList.length > 0 && (
                                  <span style={{ fontFamily: "monospace", fontSize: "0.74rem", color: "#B91C1C", marginLeft: 6 }}>
                                    ({grp.returnsList.map((r) => (r.return_number ? `#${r.return_number}` : "")).filter(Boolean).join(", ")})
                                  </span>
                                )}
                              </span>
                            </div>
                          </td>
                          <td style={{ textAlign: "center", padding: "8px 12px" }}>
                            <span
                              style={{
                                fontWeight: 800,
                                padding: "2px 8px",
                                borderRadius: 6,
                                backgroundColor: "#FEE2E2",
                                color: "#DC2626",
                                fontSize: "0.82rem",
                              }}
                            >
                              -{grp.returnedQty} Ret
                            </span>
                          </td>
                          <td style={{ textAlign: "right", padding: "8px 12px", fontWeight: 800, color: "#DC2626" }}>
                            - Rs. {formatRs(grp.returnedAmount)}
                          </td>
                        </tr>
                      )}

                      {/* Then Row: Net Delivered (Only when withReturn is true) */}
                      {hasReturn && (
                        <tr style={{ backgroundColor: "#F0FDF4", borderBottom: "1px solid #CBD5E1" }}>
                          <td></td>
                          <td colSpan={2} style={{ padding: "8px 12px" }}>
                            <div style={{ color: "#15803D", fontSize: "0.80rem", fontWeight: 800, display: "flex", alignItems: "center", gap: 6 }}>
                              <FiCheckCircle size={12} style={{ flexShrink: 0 }} />
                              <span>Net Retained / Delivered</span>
                            </div>
                          </td>
                          <td style={{ textAlign: "center", padding: "8px 12px" }}>
                            <span
                              style={{
                                fontWeight: 800,
                                padding: "2px 8px",
                                borderRadius: 6,
                                backgroundColor: "#DCFCE7",
                                color: "#15803D",
                                fontSize: "0.82rem",
                              }}
                            >
                              {grp.remainingQty}
                            </span>
                          </td>
                          <td style={{ textAlign: "right", padding: "8px 12px", fontWeight: 900, color: "#15803D" }}>
                            Rs. {formatRs(grp.remainingAmount)}
                          </td>
                        </tr>
                      )}

                      {/* (serial number) row directly underneath */}
                      <tr style={{ backgroundColor: "#F8FAFC", borderBottom: "1.5px solid var(--slate-200)" }}>
                        <td colSpan={5} style={{ padding: "8px 14px 10px 14px" }}>
                          <div style={{ display: "flex", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
                            <span
                              style={{
                                fontSize: "0.74rem",
                                fontWeight: 800,
                                color: "var(--primary)",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                                whiteSpace: "nowrap",
                                textTransform: "uppercase",
                                letterSpacing: "0.3px",
                                backgroundColor: "#DCFCE7",
                                padding: "2px 8px",
                                borderRadius: 4,
                              }}
                            >
                              <FiTag size={12} /> Serial Numbers ({displaySerials.length}):
                            </span>
                            <div
                              style={{
                                fontFamily: "monospace",
                                fontSize: "0.78rem",
                                color: "var(--navy)",
                                fontWeight: 600,
                                lineHeight: 1.6,
                                wordBreak: "break-word",
                                flex: 1,
                              }}
                            >
                              {displaySerials && displaySerials.length > 0 ? (
                                displaySerials.join(", ")
                              ) : (
                                <span style={{ color: "var(--slate-400)", fontStyle: "italic" }}>
                                  — No individual serial numbers (Bulk item) —
                                </span>
                              )}
                              {withReturn && grp.returnedSerials && grp.returnedSerials.length > 0 && (
                                <div style={{ color: "#DC2626", marginTop: 4, fontSize: "0.75rem" }}>
                                  <strong>Returned Serials ({grp.returnedSerials.length}):</strong> {grp.returnedSerials.join(", ")}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
            <tfoot>
              <tr style={{ backgroundColor: "#F8FAFC", borderTop: "2px solid var(--slate-300)" }}>
                <td colSpan={3} style={{ padding: "10px 12px", fontWeight: 800, color: "var(--navy)" }}>
                  {withReturn && summaryTotals.totalReturnedUnits > 0 ? "Total Summary (Net Delivered)" : "Total Summary"}
                </td>
                <td style={{ textAlign: "center", padding: "10px 12px", fontWeight: 900, color: "#4338CA", fontSize: "0.92rem" }}>
                  {activeUnits} Units
                </td>
                <td style={{ textAlign: "right", padding: "10px 12px", fontWeight: 900, color: "#15803D", fontSize: "0.98rem" }}>
                  Rs. {formatRs(activeTotal)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* SUMMARY / TOTAL AMOUNT SECTION */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: 20,
            marginBottom: 28,
            paddingTop: 10,
            borderTop: "1.5px solid var(--slate-200)",
          }}
        >
          <div style={{ maxWidth: 480, flex: 1 }}>
            <div style={{ fontSize: "0.74rem", color: "var(--slate-500)", fontWeight: 700, textTransform: "uppercase" }}>
              Total Amount in Words
            </div>
            <div
              style={{
                fontSize: "0.92rem",
                fontWeight: 800,
                color: "var(--navy)",
                fontStyle: "italic",
                marginTop: 2,
                marginBottom: 12,
              }}
            >
              {numberToWords(activeTotal)}
            </div>

            <div
              style={{
                fontSize: "0.76rem",
                color: "var(--slate-500)",
                backgroundColor: "#F8FAFC",
                padding: "10px 14px",
                borderRadius: 8,
                border: "1px solid var(--slate-200)",
                lineHeight: "1.5",
              }}
            >
              <strong>Delivery Verification:</strong> Items dispatched in good physical condition. Receiver is requested to verify quantities and serial numbers upon delivery.
            </div>
          </div>

          <div
            style={{
              minWidth: 280,
              backgroundColor: "#F8FAFC",
              borderRadius: 10,
              border: "1px solid var(--slate-200)",
              padding: "16px 20px",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: "0.84rem" }}>
              <span style={{ color: "var(--slate-600)" }}>Total Quantity Dispatched:</span>
              <strong style={{ color: "var(--navy)" }}>{summaryTotals.totalSoldUnits} Units</strong>
            </div>
            {withReturn && summaryTotals.totalReturnedUnits > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: "0.84rem", color: "#DC2626" }}>
                <span>Returned Quantity:</span>
                <strong>- {summaryTotals.totalReturnedUnits} Units</strong>
              </div>
            )}
            {withReturn && summaryTotals.totalReturnedUnits > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: "0.84rem", color: "#15803D" }}>
                <span>Net Delivered Quantity:</span>
                <strong>{summaryTotals.totalRemainingUnits} Units</strong>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: "0.84rem" }}>
              <span style={{ color: "var(--slate-600)" }}>Item Types / Groups:</span>
              <strong style={{ color: "var(--navy)" }}>{dcInvoiceItems.length} Groups</strong>
            </div>
            {withReturn && summaryTotals.totalReturnedAmount > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: "0.84rem", color: "#DC2626" }}>
                <span>Returned Amount:</span>
                <strong>- Rs. {formatRs(summaryTotals.totalReturnedAmount)}</strong>
              </div>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                paddingTop: 10,
                borderTop: "2px solid var(--slate-300)",
                fontSize: "1.1rem",
                fontWeight: 900,
                color: "#15803D",
              }}
            >
              <span>{withReturn && summaryTotals.totalReturnedUnits > 0 ? "Net Amount:" : "Total Amount:"}</span>
              <span>Rs. {formatRs(activeTotal)}</span>
            </div>
          </div>
        </div>

        {/* SIGNATURES */}
        <div
          className="print-signature-section"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 24,
            paddingTop: 36,
            marginTop: 24,
            borderTop: "1px dashed var(--slate-300)",
            textAlign: "center",
          }}
        >
          <div>
            <div style={{ borderBottom: "1px solid var(--slate-400)", height: 32, marginBottom: 6 }}></div>
            <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "var(--navy)" }}>Prepared / Dispatched By</div>
            <div style={{ fontSize: "0.72rem", color: "var(--slate-500)" }}>
              {invoice.created_by_name || "Commercial-IT Logistics"}
            </div>
          </div>
          <div>
            <div style={{ borderBottom: "1px solid var(--slate-400)", height: 32, marginBottom: 6 }}></div>
            <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "var(--navy)" }}>Authorized Signatory</div>
            <div style={{ fontSize: "0.72rem", color: "var(--slate-500)" }}>Commercial-IT Management</div>
          </div>
          <div>
            <div style={{ borderBottom: "1px solid var(--slate-400)", height: 32, marginBottom: 6 }}></div>
            <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "var(--navy)" }}>Receiver's Sign & Stamp</div>
            <div style={{ fontSize: "0.72rem", color: "var(--slate-500)" }}>
              {invoice.client_name || "Customer Representative"}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="sale-invoice-container" style={{ paddingBottom: 60 }}>
      {/* Print-specific style overrides */}
      <style>{`
        @media print {
          header, aside, nav, .no-print, .no-print * {
            display: none !important;
          }
          body, html {
            background: #fff !important;
            color: #000 !important;
            margin: 0 !important;
            padding: 0 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          main {
            padding: 0 !important;
            margin: 0 !important;
            max-width: 100% !important;
            overflow: visible !important;
          }
          .sale-invoice-container {
            padding: 0 !important;
            margin: 0 !important;
          }
          .invoice-paper {
            box-shadow: none !important;
            border: none !important;
            border-radius: 0 !important;
            padding: 10px 14px !important;
            margin: 0 auto !important;
            max-width: 100% !important;
            width: 100% !important;
          }
          /* Strict single-page rules for Short Invoice */
          .single-page-slip {
            box-shadow: none !important;
            border: 1px solid #475569 !important;
            border-radius: 0 !important;
            padding: 8px 12px !important;
            margin: 0 auto !important;
            max-width: 100% !important;
            width: 100% !important;
            max-height: 275mm !important;
            overflow: hidden !important;
            page-break-inside: avoid !important;
            page-break-after: avoid !important;
          }
          @page {
            size: A4 portrait;
            margin: 8mm;
          }
          .invoice-table th, .invoice-table td {
            padding: 5px 6px !important;
            font-size: 11px !important;
          }
          .compact-table th, .compact-table td {
            padding: 4px 6px !important;
            font-size: 11px !important;
            line-height: 1.2 !important;
          }
          .print-signature-section {
            display: grid !important;
            page-break-inside: avoid;
          }
          .dc-paper {
            box-shadow: none !important;
            border: 2px solid #000 !important;
            border-radius: 0 !important;
            padding: 10px 14px !important;
            margin: 0 auto !important;
            max-width: 100% !important;
            width: 100% !important;
          }
          .dc-meta-table td, .dc-items-table th, .dc-items-table td {
            border: 1.5px solid #000 !important;
            padding: 4px 6px !important;
            font-size: 11px !important;
          }
        }
      `}</style>

      {/* TOP ACTION BAR (Hidden on print) */}
      <div
        className="no-print"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 16,
          marginBottom: 18,
          padding: "16px 20px",
          backgroundColor: "#fff",
          borderRadius: 12,
          border: "1px solid var(--slate-200)",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <button
            type="button"
            onClick={() => navigate("/sales?tab=history")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 16px",
              borderRadius: 8,
              border: "1.5px solid var(--slate-300)",
              backgroundColor: "#fff",
              color: "var(--navy)",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--slate-100)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#fff")}
          >
            <FiArrowLeft size={16} /> Back to Sales
          </button>

          <div>
            <div style={{ fontSize: "0.76rem", color: "var(--slate-500)", fontWeight: 600 }}>
              Sales / Invoice Details
            </div>
            <div style={{ fontSize: "1.1rem", fontWeight: 800, color: "var(--navy)" }}>
              Invoice #{invoice.invoice_number}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {canEdit && (
            <button
              type="button"
              onClick={() => handleEditSale(invoice)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "9px 18px",
                borderRadius: 8,
                border: "1.5px solid #2563EB",
                backgroundColor: "#EFF6FF",
                color: "#1D4ED8",
                fontWeight: 700,
                fontSize: "0.86rem",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#DBEAFE")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#EFF6FF")}
            >
              <FiEdit size={16} /> Edit Sale & Account
            </button>
          )}

          <button
            type="button"
            onClick={handlePrint}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "9px 18px",
              borderRadius: 8,
              border: "none",
              backgroundColor: "var(--primary, #064E3B)",
              color: "#fff",
              fontWeight: 700,
              fontSize: "0.86rem",
              cursor: "pointer",
              boxShadow: "0 2px 6px rgba(6,78,59,0.25)",
              transition: "all 0.15s ease",
            }}
          >
            <FiPrinter size={16} /> Print / Save PDF
          </button>
        </div>
      </div>

      {/* 3-OPTION TEMPLATE SELECTOR (Detail Invoice vs Short Invoice vs DC Invoice) */}
      <div
        className="no-print"
        style={{
          backgroundColor: "#fff",
          borderRadius: 12,
          border: "1.5px solid #E2E8F0",
          padding: "16px 20px",
          marginBottom: 24,
          boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FiLayers size={18} style={{ color: "var(--primary)" }} />
            <span style={{ fontWeight: 800, fontSize: "0.95rem", color: "var(--navy)" }}>
              Choose Invoice Format (3 Options)
            </span>
          </div>
          <span style={{ fontSize: "0.78rem", color: "var(--slate-500)", fontWeight: 600 }}>
            Switch between Detail Invoice (Item-by-Item), DC Invoice (Without Return), and DC Invoice (With Return)
          </span>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 }}>
          {/* Option 1: Detail Invoice */}
          <div
            onClick={() => handleTemplateChange("detail")}
            style={{
              padding: "16px 18px",
              borderRadius: 10,
              border: selectedTemplate === "detail" ? "2px solid #064E3B" : "1.5px solid #E2E8F0",
              backgroundColor: selectedTemplate === "detail" ? "#F0FDF4" : "#F8FAFC",
              cursor: "pointer",
              transition: "all 0.15s ease",
              position: "relative",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: "#064E3B",
                    display: "inline-block",
                  }}
                />
                <span style={{ fontWeight: 800, fontSize: "0.95rem", color: "#064E3B" }}>
                  1. Detail Invoice
                </span>
              </div>
              {selectedTemplate === "detail" && (
                <span style={{ color: "#064E3B", fontWeight: 800, display: "flex", alignItems: "center", gap: 3, fontSize: "0.75rem" }}>
                  <FiCheck size={14} /> Active
                </span>
              )}
            </div>
            <p style={{ margin: 0, fontSize: "0.78rem", color: "#475569", lineHeight: 1.45 }}>
              <strong>Aak aak item ki full detail:</strong> Name, Model, Type, Serial Numbers, Part #, Qty aur Unit Price (original billed invoice).
            </p>
          </div>

          {/* Option 2: DC Invoice (Without Return) */}
          <div
            onClick={() => handleTemplateChange("dc_no_return")}
            style={{
              padding: "16px 18px",
              borderRadius: 10,
              border: (selectedTemplate === "dc_no_return" || selectedTemplate === "short") ? "2px solid #1E3A8A" : "1.5px solid #E2E8F0",
              backgroundColor: (selectedTemplate === "dc_no_return" || selectedTemplate === "short") ? "#EFF6FF" : "#F8FAFC",
              cursor: "pointer",
              transition: "all 0.15s ease",
              position: "relative",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: "#1E3A8A",
                    display: "inline-block",
                  }}
                />
                <span style={{ fontWeight: 800, fontSize: "0.95rem", color: "#1E3A8A", display: "flex", alignItems: "center", gap: 5 }}>
                  <FiTruck size={15} /> 2. DC Invoice (Without Return)
                </span>
              </div>
              {(selectedTemplate === "dc_no_return" || selectedTemplate === "short") && (
                <span style={{ color: "#1E3A8A", fontWeight: 800, display: "flex", alignItems: "center", gap: 3, fontSize: "0.75rem" }}>
                  <FiCheck size={14} /> Active
                </span>
              )}
            </div>
            <p style={{ margin: 0, fontSize: "0.78rem", color: "#475569", lineHeight: 1.45 }}>
              <strong>DC Without Return:</strong> Original Delivery Challan slip jaisa dispatch hua tha, baghair return rows ya deductions ke (Category, Descriptions, Qty, Serials & Gross Amount).
            </p>
          </div>

          {/* Option 3: DC Invoice (With Return) */}
          <div
            onClick={() => handleTemplateChange("dc")}
            style={{
              padding: "16px 18px",
              borderRadius: 10,
              border: selectedTemplate === "dc" ? "2px solid #D97706" : "1.5px solid #E2E8F0",
              backgroundColor: selectedTemplate === "dc" ? "#FFFBEB" : "#F8FAFC",
              cursor: "pointer",
              transition: "all 0.15s ease",
              position: "relative",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: "#D97706",
                    display: "inline-block",
                  }}
                />
                <span style={{ fontWeight: 800, fontSize: "0.95rem", color: "#B45309", display: "flex", alignItems: "center", gap: 5 }}>
                  <FiTruck size={15} /> 3. DC Invoice (With Return)
                </span>
              </div>
              {selectedTemplate === "dc" && (
                <span style={{ color: "#B45309", fontWeight: 800, display: "flex", alignItems: "center", gap: 3, fontSize: "0.75rem" }}>
                  <FiCheck size={14} /> Active
                </span>
              )}
            </div>
            <p style={{ margin: 0, fontSize: "0.78rem", color: "#475569", lineHeight: 1.45 }}>
              <strong>DC With Return:</strong> Delivery Challan jis mein return huay items, unka voucher #, deduction aur net delivered goods alag row mein show hotay hain.
            </p>
          </div>
        </div>
      </div>

      {/* RENDER CURRENT SELECTED TEMPLATE */}
      {selectedTemplate === "dc_no_return" || selectedTemplate === "short"
        ? renderDcInvoiceTemplate(false)
        : selectedTemplate === "dc"
          ? renderDcInvoiceTemplate(true)
          : renderDetailInvoiceTemplate()}

      {/* CONFIRM DELETE MODAL */}
      <Modal isOpen={deleteModalOpen} onClose={() => !deleting && setDeleteModalOpen(false)} maxWidth={500}>
        <div style={{ padding: "6px 4px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 10,
                backgroundColor: "#FEE2E2",
                color: "#DC2626",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <FiAlertTriangle size={24} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: "1.15rem", fontWeight: 800, color: "var(--navy)" }}>
                Delete Sale Invoice
              </h3>
              <p style={{ margin: "2px 0 0 0", fontSize: "0.82rem", color: "var(--slate-500)" }}>
                Invoice #{invoice.invoice_number}
              </p>
            </div>
          </div>

          <div
            style={{
              padding: "14px 16px",
              backgroundColor: "#FEF2F2",
              borderRadius: 8,
              border: "1px solid #FCA5A5",
              fontSize: "0.85rem",
              color: "#991B1B",
              lineHeight: 1.5,
              marginBottom: 18,
            }}
          >
            Are you sure you want to delete Sale Invoice <strong>#{invoice.invoice_number}</strong>?
            <br />
            <br />
            <strong>Note:</strong> Deleting this invoice will automatically revert all{" "}
            <strong>{invoiceItems.length} item(s)</strong> back to available inventory stock with their sale price cleared.
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button
              type="button"
              disabled={deleting}
              onClick={() => setDeleteModalOpen(false)}
              style={{
                padding: "8px 18px",
                borderRadius: 6,
                border: "1px solid var(--slate-300)",
                backgroundColor: "#fff",
                color: "var(--slate-700)",
                fontWeight: 600,
                fontSize: "0.85rem",
                cursor: deleting ? "not-allowed" : "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={deleting}
              onClick={handleDeleteInvoice}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 20px",
                borderRadius: 6,
                border: "none",
                backgroundColor: "#DC2626",
                color: "#fff",
                fontWeight: 700,
                fontSize: "0.85rem",
                cursor: deleting ? "not-allowed" : "pointer",
              }}
            >
              <FiTrash2 size={15} />
              {deleting ? "Deleting..." : "Yes, Delete Invoice"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SaleInvoicePage;
